export type MoleculeType = 'food' | 'oxygen' | 'co2' | 'gruel' | 'waste' | 'pulse';
export type MoleculeMix = MoleculeType[];

/**
 * Fluid class derived from a port/pipe's `carries` set:
 * - `gruel` if the mix contains 'gruel' OR 'waste' (both digestive — yellow-green)
 * - `blood` otherwise (red carrier — food/oxygen/co2)
 *
 * Used to choose pipe color when flowing and spurt particle colors.
 * (Waste folds into the gruel fluidClass for now — the brown `mol-waste`
 * sprite distinguishes it visually inside the same yellow-green pipe.)
 *
 * 'pulse' is a mechanical-signal MoleculeType (diaphragm → lungs). It only
 * travels through direct-link pipes (which render no segments), so its
 * fluidClass is irrelevant — we default to 'blood'.
 */
export type FluidClass = 'blood' | 'gruel';

export function fluidClassFor(mix: MoleculeMix): FluidClass {
  return mix.includes('gruel') || mix.includes('waste') ? 'gruel' : 'blood';
}

export type Direction = 'N' | 'E' | 'S' | 'W';

export type SegmentShape =
  | 'straight-h'
  | 'straight-v'
  | 'elbow-ES' // open east + south  (corner in SE quadrant)
  | 'elbow-WS' // open west + south  (corner in SW quadrant)
  | 'elbow-WN' // open west + north  (corner in NW quadrant)
  | 'elbow-EN'; // open east + north  (corner in NE quadrant)

export interface SegmentSpec {
  col: number;
  row: number;
  shape: SegmentShape;
  enteringDir: Direction; // which direction we arrived from when this segment was placed
}

export type PipeState = 'building' | 'connected' | 'flowing' | 'burst';

export interface Cell {
  col: number;
  row: number;
}

export function cellKey(c: Cell): string {
  return `${c.col},${c.row}`;
}

export function opposite(d: Direction): Direction {
  return d === 'N' ? 'S' : d === 'S' ? 'N' : d === 'E' ? 'W' : 'E';
}

export function directionFromTo(from: Cell, to: Cell): Direction | null {
  const dc = to.col - from.col;
  const dr = to.row - from.row;
  if (dc === 1 && dr === 0) return 'E';
  if (dc === -1 && dr === 0) return 'W';
  if (dc === 0 && dr === 1) return 'S';
  if (dc === 0 && dr === -1) return 'N';
  return null;
}

export function straightFor(d: Direction): SegmentShape {
  return d === 'E' || d === 'W' ? 'straight-h' : 'straight-v';
}

// Given the direction we ARRIVED from at this cell (enteringDir)
// and the direction we LEAVE toward (leavingDir), return the elbow shape.
// Returns null if the directions are colinear (i.e., straight, not elbow).
export function elbowFor(enteringDir: Direction, leavingDir: Direction): SegmentShape | null {
  // The two open ends of the elbow are: opposite(enteringDir) and leavingDir.
  const open1 = opposite(enteringDir);
  const open2 = leavingDir;
  const key = [open1, open2].sort().join('');
  switch (key) {
    case 'ES':
      return 'elbow-ES';
    case 'SW':
      return 'elbow-WS';
    case 'NW':
      return 'elbow-WN';
    case 'EN':
      return 'elbow-EN';
    // Same axis (e.g., 'EW' or 'NS') means it's actually a straight, not an elbow:
    default:
      return null;
  }
}

export function moleculeMixEqual(a: MoleculeMix, b: MoleculeMix): boolean {
  if (a.length !== b.length) return false;
  const aSet = new Set(a);
  return b.every((t) => aSet.has(t));
}

/**
 * Three-tier validity of a connected pipe, based on what the source spout carries
 * vs. what the destination receptacle accepts (primary `carries` + optional
 * `acceptsAlso` alternatives). See `PortConfig.acceptsAlso` for full semantics.
 *
 * - `correct`     — source matches one of the destination's accepted mixes exactly.
 *                   Flows; counts toward "solved"; triggers organ activation.
 * - `acceptable`  — source is a strict subset of the union of accepted mixes
 *                   (e.g., food-only into a brain that accepts food+oxygen).
 *                   Flows but does NOT activate and does NOT count toward solved.
 * - `unacceptable`— source contains at least one molecule not in the union
 *                   (e.g., gruel into a blood receptacle). Flows until first
 *                   molecule reaches the destination, then bursts there.
 */
export type PipeTier = 'correct' | 'acceptable' | 'unacceptable';

export function classifyPipe(
  sourceCarries: MoleculeMix,
  destCarries: MoleculeMix,
  destAcceptsAlso: MoleculeMix[] = [],
  destAcceptsAny: boolean = false,
): PipeTier {
  // Wildcard receptacles (e.g. intestines side-in) accept any incoming mix as
  // CORRECT. Bypasses the carries/acceptsAlso checks entirely.
  if (destAcceptsAny) return 'correct';
  const acceptedMixes = [destCarries, ...destAcceptsAlso];
  // CORRECT: source equals one of the accepted mixes exactly.
  for (const mix of acceptedMixes) {
    if (moleculeMixEqual(sourceCarries, mix)) return 'correct';
  }
  // ACCEPTABLE: every source molecule is in the union of accepted mixes.
  const union = new Set<MoleculeType>();
  for (const mix of acceptedMixes) for (const m of mix) union.add(m);
  if (sourceCarries.every((m) => union.has(m))) return 'acceptable';
  return 'unacceptable';
}
